import styled from 'styled-components';
import { Typography } from '../Typography/Typography';
import React from 'react';

// TODO: create a component and swap out existing custom RadioGroup definitions

type Variant = 'default' | 'outlined';

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ScrollableRadioGroup = styled(RadioGroup)<{ $maxHeight: string; $width?: string }>`
  overflow: auto;
  max-height: ${({ $maxHeight }) => $maxHeight};
  width: ${({ $width }) => $width || 'auto'};
  flex: 1;
`;

export const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
`;

export const RadioButton = styled.input<{ $hasError?: boolean }>`
  cursor: pointer;
  // use min-width and min-height so it never becomes squished
  min-width: 20px;
  min-height: 20px;
  // appearance: none removes the default radio svg
  appearance: none;
  background-color: var(--white);
  margin: 0;
  border: 1px solid var(--grayscale-300);
  border-radius: 50%;

  ${({ $hasError }) => ($hasError ? `border-color: var(--red-400);` : '')}

  &:checked {
    border-width: 5.5px;
  }
  &:checked:not(:disabled) {
    border-color: var(--purple-700);
  }
  &:disabled {
    cursor: not-allowed;
    border-color: var(--grayscale-100);
  }

  transition:
    border-width 0.2s ease-out,
    border-color 0.2s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Use Tailwind text-body-small-dm-sans class for typography
export const RadioLabel = styled(Typography).attrs<{ $disabled?: boolean }>(() => ({
  className: 'text-body-small-dm-sans',
}))<{ $disabled?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
`;

export const RadioBlockGroup = styled(RadioGroup)<{ $variant?: Variant }>`
  // Using && to increase CSS specificity when extending a styled component.
  // This ensures the gap override takes precedence over the base RadioGroup styles
  // without needing !important, which is more maintainable.
  && {
    gap: ${({ $variant }) => ($variant === 'outlined' ? '8px' : '4px')};
  }
  width: 100%;
`;

export const RadioBlockOption = styled(RadioOption)`
  // use min-height so the size can expand if the label is tall, e.g. due to wrapping
  min-height: 30px;
  border-radius: var(--border-radius-medium);
`;

export const RadioBlockOptionOutlined = styled(RadioBlockOption)<{ disabled?: boolean }>`
  border: 1px solid var(--grayscale-50);
  padding: 8px;
  transition: background-color 0.2s ease-out;

  &[data-checked='true'],
  &:hover {
    // TODO(tokens): Replace legacy color token --faded-purple with numbered equivalent once available.
    background-color: ${({ disabled }) => (disabled ? 'var(--grayscale-50)' : 'var(--faded-purple)')};
  }

  ${({ disabled }) => (disabled ? 'cursor: not-allowed;' : '')}
`;

type RadioOptionWrapperProps = {
  id: string;
  name: string;
  value: string;
  children: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: 'default' | 'outlined';
  hasError?: boolean;
};

export const RadioOptionWrapper = ({
  id,
  name,
  value,
  children,
  checked,
  disabled,
  onChange,
  variant = 'default',
  hasError = false,
}: RadioOptionWrapperProps) => {
  const RadioBlockComponent = variant === 'outlined' ? RadioBlockOptionOutlined : RadioBlockOption;

  return (
    <RadioBlockComponent htmlFor={id} data-checked={checked} disabled={disabled}>
      <RadioButton
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        onChange={onChange}
        $hasError={hasError}
        disabled={disabled}
      />
      {children}
    </RadioBlockComponent>
  );
};
